import React from 'react'

import { NotificationInfoFragment } from '@sketch/gql-types'
import { thumbnailForPreviewFile } from '@sketch/modules-common'

import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-16'
import { ReactComponent as AtIcon } from '@sketch/icons/at-email-16'
import { ReactComponent as ChatBubbleIcon } from '@sketch/icons/chat-bubble-16'
import { ReactComponent as CheckmarkIcon } from '@sketch/icons/checkmark-16'
import { ReactComponent as FolderIcon } from '@sketch/icons/folder-closed-17'
import { ReactComponent as StarIcon } from '@sketch/icons/star-16'
import { FolderPreview, ImagePreview, WorkspacePreview } from '../InboxListItem'

export type NotificationTypename =
  | 'AddShareMemberNotification'
  | 'CommentNotification'
  | 'DownloadableAssetRenderedNotification'
  | 'FolderArchivedNotification'
  | 'MentionNotification'
  | 'StarNotification'

export type NotificationTitle =
  | string
  | ((notification: NotificationInfoFragment) => string)

export type NotificationConfig = {
  title: NotificationTitle
  renderListItemPreview: (
    notification: NotificationInfoFragment
  ) => React.ReactNode
  typeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  reason: React.ReactNode
}

export const resolveNotificationTitle = (
  title: NotificationTitle,
  notification: NotificationInfoFragment
): string => (typeof title === 'function' ? title(notification) : title)

const REASON_THREAD = (
  <>
    You received this notification because{' '}
    <strong>you follow this thread</strong>
  </>
)

const REASON_MENTION = (
  <>
    You received this notification because <strong>you were mentioned</strong>
  </>
)

const REASON_ADMIN = (
  <>
    You received this notification because <strong>you are an admin</strong>
  </>
)

const REASON_ASSETS = (
  <>
    You received this notification because <strong>you requested assets</strong>
  </>
)

const REASON_FOLDER_ARCHIVED = (
  <>
    You received this notification because{' '}
    <strong>a folder was archived</strong>
  </>
)

const workspacePreview = (notification: NotificationInfoFragment) => (
  <WorkspacePreview
    name={notification.workspace?.name}
    src={notification.workspace?.avatar?.small}
  />
)

const sharePreview = workspacePreview

const commentMentionPreview = (notification: NotificationInfoFragment) => {
  if (
    notification.__typename !== 'CommentNotification' &&
    notification.__typename !== 'MentionNotification'
  ) {
    return sharePreview(notification)
  }

  const previewUrl = notification.previews[0]?.url
  if (!previewUrl || !notification.share) {
    return sharePreview(notification)
  }

  return (
    <ImagePreview
      src={previewUrl}
      alt={`Preview of ${notification.share.name}`}
    />
  )
}

const downloadableAssetPreview = (notification: NotificationInfoFragment) => {
  if (notification.__typename !== 'DownloadableAssetRenderedNotification') {
    return sharePreview(notification)
  }

  const share = notification.share
  const thumbnail = thumbnailForPreviewFile(
    notification.notifiedVersion?.document?.previewFile
  )

  if (!thumbnail || !share) {
    return workspacePreview(notification)
  }

  return <ImagePreview src={thumbnail} alt={`Preview of ${share.name}`} />
}

const folderPreview = (notification: NotificationInfoFragment) => {
  if (notification.__typename !== 'FolderArchivedNotification') {
    return workspacePreview(notification)
  }

  return <FolderPreview folder={notification.folder} />
}

const CONFIG: Record<NotificationTypename, NotificationConfig> = {
  CommentNotification: {
    title: 'New comment',
    renderListItemPreview: commentMentionPreview,
    typeIcon: ChatBubbleIcon,
    reason: REASON_THREAD,
  },
  MentionNotification: {
    title: 'New mention',
    renderListItemPreview: commentMentionPreview,
    typeIcon: AtIcon,
    reason: REASON_MENTION,
  },
  AddShareMemberNotification: {
    title: notification =>
      notification.user?.name
        ? `${notification.user.name} requested access to this document`
        : 'Document access request',
    renderListItemPreview: sharePreview,
    typeIcon: CheckmarkIcon,
    reason: REASON_ADMIN,
  },
  DownloadableAssetRenderedNotification: {
    title: 'Your assets are ready to download',
    renderListItemPreview: downloadableAssetPreview,
    typeIcon: DownloadIcon,
    reason: REASON_ASSETS,
  },
  FolderArchivedNotification: {
    title: 'Folder archived',
    renderListItemPreview: folderPreview,
    typeIcon: FolderIcon,
    reason: REASON_FOLDER_ARCHIVED,
  },
  StarNotification: {
    title: 'New star',
    renderListItemPreview: sharePreview,
    typeIcon: StarIcon,
    reason: REASON_THREAD,
  },
}

const FALLBACK: NotificationConfig = {
  title: 'Notification',
  renderListItemPreview: workspacePreview,
  typeIcon: ChatBubbleIcon,
  reason: REASON_THREAD,
}

export const getNotificationConfig = (
  typename: string | undefined
): NotificationConfig => {
  if (typename && typename in CONFIG) {
    return CONFIG[typename as NotificationTypename]
  }
  return FALLBACK
}
